const SCENARIO_SECTIONS = [
  { emoji: "📋", title: "Scenario Title" },
  { emoji: "🧭", title: "Situation / Context" },
  { emoji: "🎯", title: "Training Objective" },
  { emoji: "⭐", title: "Relevant Service Standards" },
  { emoji: "💬", title: "Must-use Phrases / Behaviours" },
  { emoji: "✅", title: "Expected Approach / Outcome" }
];

const convertMarkdownEmphasis = (html = "") => {
  return String(html)
    .replace(/\*{3}([^*]+)\*{3}/g, "<strong>$1</strong>")
    .replace(/\*{2}([^*]+)\*{2}/g, "<strong>$1</strong>")
    .replace(/\*{1,3}/g, "");
};

const flattenScenarioHtml = (raw = "") => {
  return convertMarkdownEmphasis(
    String(raw || "")
      .replace(/```html|```/gi, "")
      .replace(/\\r\\n|\\n|\\r/g, "<br>")
      .replace(/\r\n|\n|\r/g, "<br>")
      .replace(/\\'/g, "'")
      .replace(/\\"/g, '"')
      .replace(/height:\s*6px[^"']*/gi, "")
      .replace(/<\/(p|div|h[1-6]|li)>/gi, "<br>")
      .replace(/<li[^>]*>/gi, "• ")
      .replace(/<(p|div|h[1-6]|ul|ol)[^>]*>/gi, "")
      .replace(/<\/?(ul|ol)[^>]*>/gi, "")
      .replace(/<(?!\/?(strong|b|em|i|br)\b)[^>]+>/gi, "")
      .replace(/(<br\s*\/?>(\s|&nbsp;)*){2,}/gi, "<br>")
      .replace(/\s*<br\s*\/?>\s*/gi, "<br>")
      .trim()
  );
};

const sectionFromEmoji = (emoji) =>
  SCENARIO_SECTIONS.find((section) => section.emoji === String(emoji || "").replace(/\uFE0F/g, ""));

const parseSectionBody = (content = "", canonicalTitle = "") => {
  const titlePattern = canonicalTitle
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    .replace(/\s+/g, "\\s+");
  const leadingTitle = new RegExp(
    `^(?:<(?:strong|b)>\\s*)?${titlePattern}\\s*(?:</(?:strong|b)>)?\\s*(?:<br\\s*\\/?>)?`,
    "i"
  );

  return String(content || "")
    .replace(/^(<br\s*\/?>)+/i, "")
    .trim()
    .replace(leadingTitle, "")
    .replace(/^(<br\s*\/?>)+/i, "")
    .trim();
};

const splitBodyLines = (body = "") => {
  return String(body || "")
    .replace(/\s*•\s*/g, "<br>• ")
    .replace(/^(<br\s*\/?>)+/i, "")
    .split(/<br\s*\/?>/i)
    .map((line) => line.trim())
    .filter(Boolean);
};

export const normalizeScenarioHtml = (raw = "", { forEditor = false } = {}) => {
  const flat = flattenScenarioHtml(raw);
  if (!flat) return "";

  const emojiPattern = SCENARIO_SECTIONS
    .map((section) => section.emoji.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "\\uFE0F?")
    .join("|");
  const emojiRe = new RegExp(`(${emojiPattern})`, "g");
  const parts = flat.split(emojiRe);

  const blocks = [];
  for (let i = 1; i < parts.length; i += 2) {
    const emoji = parts[i];
    const section = sectionFromEmoji(emoji);
    if (!section) continue;

    const title = section.title;
    const lines = splitBodyLines(parseSectionBody(parts[i + 1] || "", title));
    const titleHtml = `<strong>${emoji} ${title}</strong>`;

    if (forEditor) {
      if (blocks.length) {
        blocks.push("<p><br></p>");
      }
      blocks.push(`<p>${titleHtml}</p>`);
      lines.forEach((line) => {
        blocks.push(`<p>${line}</p>`);
      });
    } else {
      if (blocks.length) {
        blocks.push("");
      }
      blocks.push(lines.length ? `${titleHtml}<br>${lines.join("<br>")}` : titleHtml);
    }
  }

  if (!blocks.length) {
    return forEditor ? `<p>${flat}</p>` : flat;
  }

  return forEditor ? blocks.join("") : blocks.join("<br>");
};

export const formatScenarioDisplayHtml = (raw = "") =>
  normalizeScenarioHtml(raw, { forEditor: false });

export const formatScenarioEditorHtml = (raw = "") =>
  normalizeScenarioHtml(raw, { forEditor: true });
